import { useState, useEffect, useRef, useCallback } from "react";
import DOMPurify from "dompurify";
import Back from "@/components/specific/Back";
import Input from "@/components/common/inputs/Input";
import TextEditor from "@/components/common/inputs/Text_Editor";
import Button from "@/components/common/Button";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate } from "react-router-dom";
import { ArrowRightOutlined } from "@/config/antd";
import Steps from "@/components/common/Steps";
import Breadcrumb from "@/components/specific/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import {
  createFund,
  selectFundLoading,
  selectFundSuccess,
  selectFundError,
} from "@/store/redux/slices/finance_management/funds_slice";
import Error from "@/components/common/alerts/Error";
import {
  fetchAccountByAny,
  selectAllAccounts,
  selectFetchedAccounts,
} from "@/store/redux/slices/finance_management/account_slice";
import { getOrganizationId } from "@/utils/token_functions";

const AddFunds = () => {
  const dispatch = useDispatch();
  useSelector(selectFundLoading);
  useSelector(selectFundSuccess);
  const fundError = useSelector(selectFundError);
  const accounts = useSelector(selectAllAccounts);
  const fetchedAccounts = useSelector(selectFetchedAccounts);

  const [formData, setFormData] = useState({
    transactionName: "",
    description: "",
    amount: "",
    debitAccount: "",
    creditAccount: "",
    paymentMode: "transfer",
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const [isFormValid, setIsFormValid] = useState(false);

  // Steps related state
  const formSections = [
    { title: "Transaction Information" },
    { title: "Amount" },
    { title: "Payment Channels" },
  ];

  const [currentSection, setCurrentSection] = useState(0);
  const [sectionsCompleted, setSectionsCompleted] = useState([]);

  const sectionRefs = useRef([]);

  // Function to check which section is currently most visible
  useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      const viewportCenter = window.scrollY + viewportHeight / 2;

      // Find the section closest to the center of the viewport
      let closestSection = 0;
      let closestDistance = Infinity;

      sectionRefs.current.forEach((sectionElement, index) => {
        if (sectionElement) {
          const rect = sectionElement.getBoundingClientRect();
          const sectionCenter = window.scrollY + rect.top + rect.height / 2;
          const distance = Math.abs(viewportCenter - sectionCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = index;
          }
        }
      });

      setCurrentSection(closestSection);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Call once on mount to set initial state

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Check if sections are completed
  const checkSectionCompletion = useCallback(() => {
    const completed = [];

    // Section 0: Transaction Information
    if (formData.transactionName && formData.description) {
      completed.push(0);
    }

    // Section 1: Amount
    if (formData.amount) {
      completed.push(1);
    }

    // Section 2: Payment Channels
    if (formData.debitAccount && formData.paymentMode) {
      completed.push(2);
    }

    setSectionsCompleted(completed);
  }, [formData]);

  // Run section completion check when form data changes
  useEffect(() => {
    checkSectionCompletion();
  }, [formData, checkSectionCompletion]);

  // Check if all required fields are filled
  useEffect(() => {
    const isValid =
      formData.transactionName?.trim() !== "" &&
      formData.description?.trim() !== "" &&
      formData.amount?.trim() !== "" &&
      formData.debitAccount?.trim() !== "" &&
      formData.paymentMode?.trim() !== "";

    setIsFormValid(isValid);
  }, [formData]);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        showLoader();
        const organizationID = getOrganizationId();
        if (organizationID) {
          await dispatch(
            fetchAccountByAny({
              key: "organizationID",
              value: organizationID,
            })
          ).unwrap();
        }
      } catch (error) {
        console.error("Error fetching accounts:", error);
      } finally {
        hideLoader();
      }
    };

    fetchAccounts();
  }, [dispatch, hideLoader, showLoader]);

  // Transform accounts for dropdown with better error handling
  const accountOptions =
    Array.isArray(accounts) && accounts.length > 0
      ? accounts.map((account) => ({
          value: account._id,
          label: `${account.bankName || "Unknown Bank"} - ${
            account.accountNumber || "No Account Number"
          }`,
        }))
      : [];

  // Use fetchedAccounts as fallback if accounts is empty
  const finalAccountOptions =
    accountOptions.length > 0
      ? accountOptions
      : Array.isArray(fetchedAccounts) && fetchedAccounts.length > 0
      ? fetchedAccounts.map((account) => ({
          value: account._id,
          label: `${account.bankName || "Unknown Bank"} - ${
            account.accountNumber || "No Account Number"
          }`,
        }))
      : [];

  // Payment mode options
  const paymentModeOptions = [
    { value: "transfer", label: "Bank Transfer" },
    { value: "card", label: "Card Payment" },
  ];

  const handleProceed = async () => {
    showLoader();
    try {
      // Sanitize the description using DOMPurify
      const sanitizedDescription = DOMPurify.sanitize(formData.description, {
        ALLOWED_TAGS: [], // Remove all HTML tags
      });

      const sanitizedFormData = {
        ...formData,
        description: sanitizedDescription,
        organizationID: getOrganizationId(),
      };

      console.log("sanitized form data to be sent", sanitizedFormData);

      await dispatch(createFund(sanitizedFormData)).unwrap();
      hideLoader();
      setShowSuccess(true);
    } catch {
      hideLoader();
      setShowError(true);
    }
  };

  // Succee close
  const handleSuccessClose = () => {
    showLoader();
    setTimeout(() => {
      hideLoader();
      setShowSuccess(false);
      navigate("/dashboard/financial");
    }, 2000);
  };

  const handleErrorClose = () => {
    setShowError(false);
  };

  const handleInputChange = (field) => (e) => {
    let value;

    // Handle different types of input changes
    if (e === null) {
      value = ""; // Handle clearing of select inputs
    } else if (typeof e === "object" && e?.target) {
      value = e.target.value; // Handle regular input changes
    } else {
      value = e; // Handle select input changes
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditorChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value,
    }));
  };

  // Define breadcrumb items
  const breadcrumbItems = [
    { title: "Financial Manager", link: "/dashboard/financial" },
    { title: "Add Funds", link: null },
  ];

  return (
    <div className="w-full grid-cols-4 gap-10 py-6 text-left sm:py-0 lg:grid">
      <div className="col-span-3 lg:border-r">
        <Breadcrumb items={breadcrumbItems} />
        {/* Back Navigation */}
        <Back text="Back" />

        <div className="my-5 rounded-lg lg:p-5 lg:border">
          {/* Header Section */}
          <div className="mb-4">
            <h1 className="heading-medium text-lighterGrey">
              Add Funds to your Account
            </h1>
            <p className="mt-2 text-small text-lighterGrey">
              Fill the form below to add funds to your account. All fields are
              required.
            </p>
          </div>

          {/* Transaction Details Section */}
          <div
            ref={(element) => {
              sectionRefs.current[0] = element;
            }}
            className="mt-8">
            <h2 className="mb-6 text-bold text-lighterGrey">
              Describe what this transaction is about
            </h2>

            <div className="space-y-6">
              <Input
                label="TRANSACTION NAME"
                placeholder="Enter transaction name"
                type="text"
                name="transactionName"
                value={formData.transactionName}
                onChange={handleInputChange("transactionName")}
              />

              <TextEditor
                label="TRANSACTION DESCRIPTION"
                placeholder="Enter transaction description"
                value={formData.description}
                onChange={handleEditorChange}
              />
            </div>
          </div>

          {/* Amount Section */}
          <div
            ref={(element) => {
              sectionRefs.current[1] = element;
            }}
            className="mt-10">
            <h2 className="text-bold text-lighterGrey">
              Amount to be Deposited
            </h2>
            <p className="mt-2 mb-6 text-regular text-lighterGrey">
              The figure will be then be added to your account balance excluding
              your monthly budget limit.
            </p>

            <Input
              label="TRANSACTION AMOUNT (₦)"
              placeholder="0.00"
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange("amount")}
              currencyFormat={true}
            />
          </div>

          {/* Payment Details Section */}
          <div
            ref={(element) => {
              sectionRefs.current[2] = element;
            }}
            className="mt-10">
            <h2 className="text-bold text-lighterGrey">Transaction Channel</h2>
            <p className="mt-2 mb-6 text-regular text-lighterGrey">
              Where is this funds being sent from and where is it being
              deposited?
            </p>

            <div className="space-y-4">
              <Input
                type="select"
                label="SELECT ACCOUNT"
                value={formData.debitAccount}
                onChange={handleInputChange("debitAccount")}
                options={finalAccountOptions}
                placeholder="Select account"
              />

              <Input
                type="select"
                label="PAYMENT MODE"
                value={formData.paymentMode}
                onChange={handleInputChange("paymentMode")}
                options={paymentModeOptions}
                placeholder="Select payment mode"
              />
            </div>

            {/* Proceed Button */}
            <Button
              text="Proceed"
              icon={<ArrowRightOutlined />}
              onClick={handleProceed}
              disabled={!isFormValid}
              color="primary"
              className={`
              w-full mt-8
              ${!isFormValid && "bg-darkBlue text-primary cursor-not-allowed"}
            `}
              customBgColor={isFormValid ? "primary" : "darkBlue"}
              customTextColor={isFormValid ? "white" : "darkBlue"}
            />
          </div>
        </div>
      </div>
      <div className="hidden mt-10 lg:block h-[90vh] sticky top-[80px]">
        <Steps
          current={currentSection}
          steps={formSections}
          mode="single-page"
          sectionsCompleted={sectionsCompleted}
        />
      </div>

      {/* Success Dialog */}
      <Success
        isVisible={showSuccess}
        onClose={() => setShowSuccess(false)}
        header="Funds Added"
        paragraph="Funds have been added to your account"
        buttonText="Continue"
        onButtonClick={handleSuccessClose}
      />

      {/* Error Dialog */}
      <Error
        isVisible={showError}
        onClose={handleErrorClose}
        header="Failed to Add Funds"
        paragraph={
          fundError?.message ||
          "An error occurred while adding funds. Please try again."
        }
        buttonText="Try Again"
        recoveryPath="/dashboard/financial/add-funds"
      />
    </div>
  );
};

export default AddFunds;
